import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

const rewardItems = [
  {
    id: "linkedin-post",
    title: "Make a post using this draft to LinkedIn",
    subtitle: "Got the offer? Brag about it. Post your career wins with Nora on LinkedIn.",
    reward: "$5",
    accent: "blue",
    status: "default",
    icon: <ShareOutlinedIcon />,
    expandable: true,
  },
  {
    id: "reddit-post",
    title: "Make a post using this draft to Reddit",
    subtitle: "Nora been helpful? Nailed an interview? Share your journey on Reddit.",
    reward: "$5",
    accent: "orange",
    status: "default",
    icon: <ForumOutlinedIcon />,
    expandable: true,
  },
];

function ReferralRewardsModal({ open, onClose }) {
  const [expandedItem, setExpandedItem] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [linkedinDraftCopied, setLinkedinDraftCopied] = useState(false);
  const [redditDraftCopied, setRedditDraftCopied] = useState(false);
  
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [redditUrl, setRedditUrl] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [referralLink, setReferralLink] = useState("");

  useEffect(() => {
    const userId = localStorage.getItem("userId") || "";
    const userName = localStorage.getItem("userName") || "";
    setReferralLink(`https://interview.norahq.com/referral/${userId}/${encodeURIComponent(userName)}`);
  }, [open]);

  const validateUrl = (platform, url) => {
    if (platform === "LinkedIn" && !url.includes("linkedin.com")) {
      setVerifyError("Please enter a valid LinkedIn post URL.");
      return false;
    }
    if (platform === "Reddit" && !url.includes("reddit.com")) {
      setVerifyError("Please enter a valid Reddit post URL.");
      return false;
    }
    setVerifyError("");
    return true;
  };

  const handleVerify = async (platform, url) => {
    if (!url) return;
    if (!validateUrl(platform, url)) return;

    const userId = localStorage.getItem("userId") || "unknown";
    const userEmail = localStorage.getItem("userEmail") || "unknown";
    const date = new Date().toLocaleString();

    const message = {
      text: `*New ${platform} Referral *\n*User ID:* ${userId}\n*Email:* ${userEmail}\n*Post URL:* ${url}\n*Date:* ${date}`
    };

    try {
      await fetch(`${process.env.REACT_APP_API_ENDPOINT}/Chat/notifySlack`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: message.text }),
      });
      alert("Submitted! We'll verify and send your reward shortly.");
    } catch (err) {
      console.error("Slack notify failed:", err);
    }
  };

  const linkedinPostDraft = useMemo(
  () =>
    `Glad to announce - I'm joining [Company] as a [Role] 🎉

    Huge thank you to my friends and family who put up with me stress-talking about interviews for the past few months lol. And to everyone who helped prepare and do mock interviews with me - you already know!

    Speaking of which, Nora AI was genuinely clutch for interview prep. Catches things about your answers you'd never notice on your own.

    If you're in the middle of it right now, keep going. It's worth it 🙏

    #NewJob #[Industry]`,
      []
  );

  const redditPostDraft = useMemo(
  () =>
    `been prepping for interviews for the past few weeks and tried a bunch of tools. wanted to share what actually worked for me.

    tried the usual stuff first - youtube videos, chatgpt, my friends for mock questions. helpful but it never felt like a real interview and didn't get the feedback I wanted. 

    then i found Nora AI. it does voice-based mock interviews - you actually talk through your answers like a real interview. you upload your resume and the role you're applying for and it tailors the questions to that. it even asks follow-up questions which threw me off at first lol.

    what i liked:
    - felt way more realistic than typing answers
    - the feedback actually breaks down soft skills + technical gaps, not just "good job"
    - you can re-do interviews as many times as you want
    - $15/month which is way cheaper than a real interview coach

    cracked my interviews last week. not saying it's the only reason but the reps definitely helped.

    anyone else using AI tools for interview prep rn? curious what's working`,
      []
    );

  useEffect(() => {
    if (!open) {
      setLinkedinDraftCopied(false);
      setRedditDraftCopied(false);
      setReferralCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  const copyText = async (value, setCopiedState) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedState(true);
      window.setTimeout(() => setCopiedState(false), 1800);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="referral-modal-overlay" onClick={onClose}>
      <div
        className="referral-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="referral-modal-title"
      >
        <button
          className="referral-modal-close"
          onClick={onClose}
          aria-label="Close referral rewards modal"
        >
          <CloseIcon />
        </button>

        <div className="referral-modal-header">
          <div className="referral-modal-header-icon">
            <CardGiftcardIcon />
          </div>
          <div className="referral-modal-header-copy">
            <h2 id="referral-modal-title">Win Rewards &amp; Spread the Word!</h2>
            <p>
              Every time you complete one of the items below, we&apos;ll send you a gift
              card worth that amount.
            </p>
          </div>
        </div>

        <div className="referral-reward-list">
          {rewardItems.map((item) => {
            const isExpanded = expandedItem === item.id;

            return (
              <div
                key={item.id}
                className={[
                  "referral-reward-card",
                  `referral-reward-card--${item.accent}`,
                  item.status === "applied" ? "referral-reward-card--applied" : "",
                  isExpanded ? "referral-reward-card--expanded" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <button
                  className="referral-reward-summary"
                  type="button"
                  onClick={() =>
                    item.expandable
                      ? setExpandedItem(isExpanded ? null : item.id)
                      : undefined
                  }
                  disabled={!item.expandable}
                >
                  <div className={`referral-reward-icon referral-reward-icon--${item.accent}`}>
                    {item.icon}
                  </div>
                  <div className="referral-reward-copy">
                    <h3>{item.title}</h3>
                    <p>{item.subtitle}</p>
                  </div>
                  <div className="referral-reward-meta">
                    <span className="referral-reward-amount">{item.reward}</span>
                    {item.status === "applied" ? (
                      <span className="referral-reward-pill">Applied</span>
                    ) : item.expandable ? (
                      isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />
                    ) : (
                      <KeyboardArrowRightIcon />
                    )}
                  </div>
                </button>

                {/* LinkedIn expanded detail */}
                {item.id === "linkedin-post" && isExpanded && (
                  <div className="referral-reward-detail">
                    <p className="referral-detail-intro">
                      Make a LinkedIn post about our AI Mock Interviewer, Nora to
                      receive a discount.
                    </p>

                    <div className="referral-draft-card">
                      <div className="referral-draft-header">
                        <div className="referral-draft-title">
                          <AutoAwesomeIcon />
                          <span>AI-Generated Post Draft</span>
                        </div>
                        <div className="referral-draft-actions">
                          <button
                            type="button"
                            aria-label="Copy post draft"
                            onClick={() => copyText(linkedinPostDraft, setLinkedinDraftCopied)}
                          >
                            <ContentCopyIcon />
                          </button>
                        </div>
                      </div>

                      <div className="referral-draft-body">{linkedinPostDraft}</div>
                      {linkedinDraftCopied && (
                        <div className="referral-copy-state">Draft copied</div>
                      )}
                    </div>

                    <div className="referral-claim-card">
                      <h4>How to claim your discount:</h4>
                      <ol>
                        <li>Copy the draft above (customize it if you&apos;d like)</li>
                        <li>Post it on your LinkedIn</li>
                        <li>Paste the link to your post below</li>
                      </ol>
                    </div>

                    <div className="referral-url-section">
                      <label htmlFor="linkedin-post-url">LinkedIn Post URL</label>
                      <div className="referral-url-row">
                        <div className="referral-url-input-wrap">
                          <LinkIcon />
                          <input
                            id="linkedin-post-url"
                            type="text"
                            placeholder="https://www.linkedin.com/posts/..."
                            value={linkedinUrl}
                            onChange={(e) => setLinkedinUrl(e.target.value)}
                          />
                        </div>
                        <button type="button" className="referral-verify-button"
                          onClick={() => handleVerify("LinkedIn", linkedinUrl)}>
                          Verify
                        </button>
                      </div>
                      {verifyError && <p style={{ color: "red", fontSize: "13px", marginTop: "8px" }}>{verifyError}</p>}
                    </div>
                  </div>
                )}

                {/* Reddit expanded detail */}
                {item.id === "reddit-post" && isExpanded && (
                  <div className="referral-reward-detail">
                    <p className="referral-detail-intro">
                      Share your experience with Nora in a relevant subreddit (e.g.{" "}
                      <strong>r/interviews</strong>, <strong>r/jobsearchhacks</strong>, or{" "}
                      <strong>r/recruiting</strong>) to receive a discount.
                    </p>

                    <div className="referral-draft-card">
                      <div className="referral-draft-header">
                        <div className="referral-draft-title">
                          <AutoAwesomeIcon />
                          <span>AI-Generated Post Draft</span>
                        </div>
                        <div className="referral-draft-actions">
                          <button
                            type="button"
                            aria-label="Copy post draft"
                            onClick={() => copyText(redditPostDraft, setRedditDraftCopied)}
                          >
                            <ContentCopyIcon />
                          </button>
                        </div>
                      </div>

                      <div className="referral-draft-body">{redditPostDraft}</div>
                      {redditDraftCopied && (
                        <div className="referral-copy-state">Draft copied</div>
                      )}
                    </div>

                    <div className="referral-claim-card">
                      <h4>How to claim your discount:</h4>
                      <ol>
                        <li>Copy the draft above (customize it if you&apos;d like)</li>
                        <li>Post it in a relevant subreddit on Reddit</li>
                        <li>Paste the link to your post below</li>
                      </ol>
                    </div>

                    <div className="referral-url-section">
                      <label htmlFor="reddit-post-url">Reddit Post URL</label>
                      <div className="referral-url-row">
                        <div className="referral-url-input-wrap">
                          <LinkIcon />
                          <input
                            id="reddit-post-url"
                            type="text"
                            placeholder="https://www.reddit.com/r/.../post/..."
                            value={redditUrl}
                            onChange={(e) => setRedditUrl(e.target.value)}
                          />
                        </div>
                        <button type="button" className="referral-verify-button"
                          onClick={() => handleVerify("Reddit", redditUrl)}>
                          Verify
                        </button>
                      </div>
                      {verifyError && <p style={{ color: "red", fontSize: "13px", marginTop: "8px" }}>{verifyError}</p>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

export default ReferralRewardsModal;